import { all, put, call, retry, takeEvery, takeLatest, select } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { inboxActions, siteActions } from '../actions';
import { getAccessToken } from './authentication.sagas';
import { InboxCountType, InboxList } from '../modules/inbox/types';
import { InboxListActionType, UpdateInboxStatusActionType } from '../modules/inbox/types';
import { Api, API_CONFIGURATIONS } from '../services/services';
import { Form } from '../modules/application/types';
import defaultInboxSearchForm from '../../views/Inbox/defaultInboxSearchForm.json';
import { getInboxSearchFormId } from '../selectors/site.selectors';

function* getInboxList(action: InboxListActionType) {
    try {
        yield put(inboxActions.getInbox.pending);

        const token: string = yield call(getAccessToken, 'applications.read');

        if (action?.data?.skip === 0) {
            const inboxCount: InboxCountType = {
                totalInboxCount: 0,
            };
            // get total count of inbox items
            const countTotal: number = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getInboxCount,
                token,
                action?.payload?.data,
            );
            inboxCount.totalInboxCount = countTotal;

            yield put(inboxActions.getInboxListCount.success(inboxCount));
        }

        const response: InboxList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getInboxList,
            token,
            action?.data,
            action?.payload?.data,
        );

        yield put(inboxActions.getInbox.success(response, { limit: action.data.limit, skip: action.data.skip }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inboxActions.getInbox.error(error));
    }
}

function* getInboxSearchForm() {
    try {
        let inboxSearchForm: Form = defaultInboxSearchForm;
        yield put(inboxActions.getInboxSearchForm.pending);
        const token: string = yield call(getAccessToken, 'forms.read');
        const formId: string = yield select(getInboxSearchFormId);
        if (!!formId) {
            try {
                inboxSearchForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    token,
                    formId,
                );
            } catch (error) {
                yield put(inboxActions.getInboxSearchForm.error(error));
            }
        }
        yield put(inboxActions.getInboxSearchForm.success(inboxSearchForm));
    } catch (error) {
        yield put(siteActions.setError(error));
    }
}

function* applyInboxFilter(action: InboxListActionType) {
    yield getInboxList(action);
}

function* updateInboxStatus(action: UpdateInboxStatusActionType) {
    const { data, id } = action.payload;
    try {
        yield put(inboxActions.updateInboxStatus.pending);
        const token: string = yield call(getAccessToken, 'applications.write');
        const status: number = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateInboxStatus,
            token,
            id,
            data,
        );

        if (status === 204) {
            yield put(inboxActions.updateInboxStatus.success(id));
            const message = `${
                data.status === 'Approved'
                    ? 'Request has been approved.'
                    : data.status === 'Denied' && 'Request has been denied.'
            }`;

            yield put(
                siteActions.setSuccess({
                    isError: false,
                    errorCode: 0,
                    message,
                }),
            );
        }
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(inboxActions.updateInboxStatus.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_INBOX_LIST_BEGIN, getInboxList),
        takeEvery(ACTIONS.GET_INBOX_SEARCH_FORM_BEGIN, getInboxSearchForm),
        takeEvery(ACTIONS.UPDATE_INBOX_STATUS_BEGIN, updateInboxStatus),
        takeLatest(ACTIONS.APPLY_INBOX_FILTERS, applyInboxFilter),
    ]);
}
